import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  width: 100%;
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 2rem 0;
  position: relative;
  
  @media print {
    padding: 0;
    max-width: 100%;
    height: 100%;
    overflow: hidden;
  }
`;

const ResumeContent = styled.div`
  padding: 0 5%;
  margin-top: 0rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
  
  @media print {
    margin-top: 0.5rem;
    padding: 0.5in 0.5in;
    font-size: 10pt;
    max-height: 11in;
    overflow: hidden;
  }
`;

const ResumeHeader = styled.div`
  display: none;
  
  @media print {
    display: block;
    text-align: center;
    margin-bottom: 1rem;
  }
`;

const PersonName = styled.h1`
  font-size: 24pt;
  font-weight: bold;
  margin-bottom: 0.25rem;
  line-height: 1.1;
`;

const ContactInfo = styled.div`
  font-size: 10pt;
  color: #555;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  
  @media print {
    gap: 0.8rem;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  
  i {
    font-size: 0.9rem;
  }
  
  @media print {
    gap: 0.2rem;
    
    i {
      font-size: 0.8rem;
    }
  }
`;

const Section = styled(motion.section)`
  margin-bottom: 3rem;
  
  @media print {
    margin-bottom: 1.25rem;
    page-break-inside: avoid;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--accent-color);
  
  @media print {
    font-size: 14pt;
    margin-bottom: 0.4rem;
    padding-bottom: 0.2rem;
  }
`;

const ExperienceItem = styled.div`
  margin-bottom: 2rem;
  
  @media print {
    margin-bottom: 0.8rem;
    page-break-inside: avoid;
  }
`;

const ExperienceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  
  @media print {
    margin-bottom: 0.1rem;
  }
`;

const JobInfo = styled.div``;

const JobTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  
  @media print {
    font-size: 11pt;
    margin-bottom: 0.15rem;
  }
`;

const Company = styled.h4`
  font-size: 1.2rem;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
  
  @media print {
    font-size: 10pt;
    margin-bottom: 0.15rem;
  }
`;

const Period = styled.p`
  font-size: 1rem;
  font-style: italic;
  text-align: right;
  
  @media print {
    font-size: 10pt;
  }
`;

const Description = styled.div`
  font-size: 1rem;
  line-height: 1.6;

  ul {
    margin-top: 0.5rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
  
  @media print {
    font-size: 9pt;
    line-height: 1.3;
    
    ul {
      margin-top: 0.1rem;
      padding-left: 1.2rem;
    }
    
    li {
      margin-bottom: 0.15rem;
    }
  }
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media print {
    gap: 0.6rem;
  }
`;

const Skill = styled.div`
  background-color: #f5f5f5;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 1rem;
  
  @media print {
    font-size: 9pt;
    padding: 0.2rem 0.5rem;
    background-color: #f5f5f5 !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
`;

const Education = styled.div`
  margin-bottom: 1.5rem;
  
  @media print {
    margin-bottom: 0.8rem;
    page-break-inside: avoid;
  }
`;

const EducationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const EducationInfo = styled.div`
  max-width: 80%;
`;

const Degree = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.3rem;
  
  @media print {
    font-size: 11pt;
    margin-bottom: 0.1rem;
  }
`;

const School = styled.h4`
  font-size: 1.1rem;
  color: var(--secondary-color);
  font-weight: normal;
  
  @media print {
    font-size: 9pt;
  }
`;

const PrintButtons = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  gap: 1rem;
  z-index: 100;
  
  @media print {
    display: none;
  }
`;

const PrintButton = styled.button`
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--accent-hover-color, #0056b3);
  }
`;

// Add a specialized component for print compression
const CompressedList = styled.ul`
  @media print {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
`;

const Resume: React.FC = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleDownloadPDF = () => {
    // Set print media to simulate print
    const style = document.createElement('style');
    style.textContent = `
      @media screen {
        body { visibility: hidden; }
        .resume-content-wrapper { visibility: visible; position: absolute; left: 0; top: 0; }
        nav, footer, button, .header, .footer { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    
    // Prepare for print
    const originalTitle = document.title;
    document.title = "Resume.pdf";
    
    // Print as PDF
    window.print();
    
    // Clean up
    document.title = originalTitle;
    document.head.removeChild(style);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body, html {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100%;
              color: black;
              background-color: white;
              font-size: 10pt;
              line-height: 1.3;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            @page {
              size: letter portrait;
              margin: 0;
              scale: 95%;
            }
            nav, footer, button, .header, .footer {
              display: none !important;
            }
            * {
              box-shadow: none !important;
              text-shadow: none !important;
            }
            strong {
              font-weight: bold !important;
            }
            .motion-div > section:last-child {
              margin-bottom: 0 !important;
            }
            .resume-item {
              break-inside: avoid;
              margin-bottom: 1rem !important;
            }
            p, h1, h2, h3, h4, h5, h6 {
              margin-top: 0 !important;
              margin-bottom: 0.3rem !important;
            }
            ul, ol {
              margin: 0.3rem 0 !important;
              padding-left: 1rem !important;
            }
            li {
              line-height: 1.4 !important;
              margin-bottom: 0.25rem !important;
            }
            .resume-content-wrapper {
              padding-top: 0.4in !important;
            }
            section {
              margin-bottom: 1.5rem !important;
            }
          }
        `}
      </style>
      <PageContainer>
        <ResumeContent className="resume-content-wrapper">
          <ResumeHeader>
            <PersonName>Alan Nuñez</PersonName>
            <ContactInfo>
            
              <ContactItem>
                <i className="fas fa-envelope"></i>
                alannunez1679@gmail.com
              </ContactItem>
              <ContactItem>
                <i className="fas fa-globe"></i>
                www.nunezalan.com
              </ContactItem>
              <ContactItem>
                <i className="fas fa-phone"></i>
                (914) 346-7640
              </ContactItem>
              
            </ContactInfo>
          </ResumeHeader>
          
          <motion.div
            className="motion-div"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <Section variants={item}>
              <SectionTitle>Education</SectionTitle>
              <Education>
                <EducationHeader>
                  <EducationInfo>
                    <Degree>Master of Science in Robotics</Degree>
                    <School>University of Pennsylvania, School of Engineering and Applied Science</School>
                  </EducationInfo>
                  <Period>2021</Period>
                </EducationHeader>
              </Education>
              
              <Education>
                <EducationHeader>
                  <EducationInfo>
                    <Degree>Bachelor of Science in Mechanical Engineering & Applied Mechanics</Degree>
                    <School>University of Pennsylvania, School of Engineering and Applied Science</School>
                  </EducationInfo>
                  <Period>2021</Period>
                </EducationHeader>
              </Education>
            </Section>

            <Section variants={item}>
              <SectionTitle>Experience & Projects</SectionTitle>
              
              <ExperienceItem className="resume-item">
                <ExperienceHeader>
                  <JobInfo>
                    <JobTitle>Founder & Developer</JobTitle>
                    <Company>Chore Focus</Company>
                  </JobInfo>
                  <Period>2023 - Present</Period>
                </ExperienceHeader>
                <Description>
                  <CompressedList>
                    <li>Design, develop, and maintain an AI-powered chore-tracking app with <strong>6,000+ monthly active users</strong></li>
                    {/* <li>Implement LLM-based natural language processing for intelligent chore categorization and scheduling recommendations</li> */}
                    <li>Integrate OpenAI API for conversational interfaces and automated task generation from user input</li>
                    <li>Build intelligent agent workflows using LangGraph for multi-step task orchestration</li>
                    {/* <li>Deploy real-time data processing pipelines for user behavior analysis and personalized experiences</li> */}
                    <li>Build full-stack architecture using Flutter frontend, Firebase backend, and cloud-based AI services</li>
                    
                    {/* <li>Develop A/B testing frameworks to optimize AI-driven features and improve user engagement metrics</li> */}
                    
                    
    
                  </CompressedList>
                </Description>
              </ExperienceItem>

              <ExperienceItem className="resume-item">
                <ExperienceHeader>
                  <JobInfo>
                    <JobTitle>Synthetic Data Generation & AI Object Detection</JobTitle>
                    <Company>Machine Learning Project</Company>
                  </JobInfo>
                  <Period>2025</Period>
                </ExperienceHeader>
                <Description>
                  <CompressedList>
                    <li>Generated 1,000 photorealistic images of condiment bottles with automated bounding-box labels using AI-driven rendering</li>
                    <li>Designed domain randomization strategies to bridge sim-to-real gap and improve model generalization</li>
                    
                    
                    <li>Implemented data preprocessing pipelines and evaluation frameworks for model performance optimization</li>
                    <li>Trained and fine-tuned YOLOv11 object detection model, achieving <strong>92% accuracy on live camera feed</strong></li>
                  </CompressedList>
                </Description>
              </ExperienceItem>

              <ExperienceItem className="resume-item">
                <ExperienceHeader>
                  <JobInfo>
                    <JobTitle>Structure from Motion</JobTitle>
                    <Company>Computer Vision Project</Company>
                  </JobInfo>
                  <Period>2024</Period>
                </ExperienceHeader>
                <Description>
                  <CompressedList>
                    <li>Built complete 3D reconstruction pipeline from video using C++, COLMAP, and OpenCV</li>
                    <li>Implemented feature matching using SIFT descriptors and RANSAC for robust correspondence across frames</li>
                    
                    <li>Benchmarked traditional feature matching against deep learning approaches, analyzing trade-offs between computational efficiency and accuracy for real-time applications</li>
                  
                    <li>Achieved <strong>sub-5 cm positional error across 100+ frames</strong> through optimized pose estimation and triangulation algorithms</li>                  </CompressedList>
                </Description>
              </ExperienceItem> 

              <ExperienceItem className="resume-item">
                <ExperienceHeader>
                  <JobInfo>
                    <JobTitle>Mechanical Engineer</JobTitle>
                    <Company>Nuro</Company>
                  </JobInfo>
                  <Period>2021 - 2023</Period>
                </ExperienceHeader>
                <Description>
                  <CompressedList>
                    <li>Owned remote control mechanical design focusing on improving ergonomics for operators controlling robots during maintenance and deployment</li>
                    {/* <li>Iterated the 3D design of an ergonomic plastic part with complex curvature </li> */}
                    {/* <li>Created over 10 3D prints and 15 injection-molded prototypes</li> */}
                    <li>Produced detailed 2D drawings with GD&T per ASME Y14.5, ensuring precise manufacturing and assembly</li>
                    {/* <li>Collaborated closely with the GSM team to source and procure components</li> */}
                    {/* <li>Collaborated with cross-functional teams to source and procure components</li> */}
                    <li>Interfaced with overseas vendors for DFM review and production kickoff of mechanical and electronic components</li>
                    <li>Improved the door mechanism design for a delivery vehicle prototype, <strong>extending its operational lifespan from 1,000 to 20,000 cycles</strong></li>
                    <li>Created and conducted a user study with 15 depot users to validate user-centered designs, resulting in a <strong>14% increase in average user satisfaction</strong></li>
                  </CompressedList>
                </Description>
              </ExperienceItem>

            </Section>
            
            
            
            <Section variants={item}>
              <SectionTitle>Skills</SectionTitle>
              <SkillsContainer>
                {/* Full-Stack Development */}
                <Skill>React</Skill>
                <Skill>React Native</Skill>
                <Skill>Node.js</Skill>
                <Skill>JavaScript</Skill>
                
                {/* AI/ML Core Skills */}
                <Skill>Python</Skill>
                <Skill>LangChain</Skill>
                <Skill>LangGraph</Skill>
                <Skill>RAG</Skill>
                <Skill>LangSmith</Skill>
                <Skill>Cursor AI</Skill>
                <Skill>OpenAI API</Skill>
                <Skill>PyTorch</Skill>
                <Skill>TensorFlow</Skill>
                <Skill>OpenCV</Skill>
                
                
                
                {/* Cloud & DevOps */}
                <Skill>Google Cloud</Skill>
                <Skill>Docker</Skill>
                <Skill>RESTful APIs</Skill>
                
                {/* Data & Analysis */}
                <Skill>Pandas</Skill>
                {/* <Skill>MATLAB</Skill> */}
                <Skill>C++</Skill>
                
                {/* Engineering Tools */}
                {/* <Skill>SolidWorks</Skill> */}
                {/* <Skill>NX</Skill> */}
                {/* <Skill>GD&T</Skill> */}
                {/* <Skill>DFM</Skill> */}
              </SkillsContainer>
            </Section>
          </motion.div>
        </ResumeContent>
      </PageContainer>
    </>
  );
};

export default Resume; 